import React, {useEffect, useState} from 'react'
import Chart from "react-apexcharts";
import {FutureCalc, IIncomeDetail} from "../../../../components/calculator/Calculator";
import {numberToMoneyFormat} from "../../../../stores/StoreUtilities";
import {Paper, TableContainer, useTheme} from "@mui/material";
import cloneDeep from "lodash.clonedeep";
import Income, {IncomeType} from "../../../../model/Income";

const defaultOptions = {
  chart: {
    id: "budget-projection",
    type: 'bar',
    stacked: true,
    height: 350,
    width: "100%",
    // zoom: {
    //   autoScaleYaxis: true
    // },
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    },
    zoom: {
      enabled: false,
      autoScaleYaxis: true
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    }
  },
  title: {
    text: "",
    align: "right",
    margin: 0,
    floating: true,
    style: {
      fontSize: "16px",
      fontFamily: "Poppins,sans-serif",
      color: "rgb(31, 92, 167)"
    }
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    position: 'top', // TODO: Figure out why this isn't working
    horizontalAlign: 'left',
    showForSingleSeries: true,
    onItemClick: {
      toggleDataSeries: false
    }
  },
  xaxis: {
    type: 'number',
    min: 2000,
    axisBorder: {
      show: true
    }
  },
  yaxis: {
    labels: {
      formatter: (val: number) => {
        return numberToMoneyFormat(val, 0)
        // if (val < 1000000) {
        //   return `$${val / 1000}K`
        // } else {
        //   return `$${val / 1000000}M`
        // }
      }
    }
  },
  tooltip: {
    y: {
      formatter: function (value: number) {
        return numberToMoneyFormat(value, 0)
      }
    }
  },
  // tooltip: {
  //   x: {
  //     format: 'dd MMM yyyy'
  //   }
  // },
  colors: [],
  // fill: {
  //   type: 'gradient',
  //   gradient: {
  //     shadeIntensity: 1,
  //     opacityFrom: 0.7,
  //     opacityTo: 0.9,
  //     stops: [0, 100]
  //   }
  // },
  // stroke: {
  //   width: 3
  // },
  resonsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const SocialSecurityBenefitChart = ({
  year,
  futureCalc,
  onChange,
  height,
  width,
  title
}: {
  year: number
  futureCalc: FutureCalc
  onChange?: (year: number) => any
  height?: string
  width?: string
  title?: string
}) => {
  const [startYear, setStartYear] = useState<number>(0)
  const [endYear, setEndYear] = useState<number>(0)
  const [calc, setCalc] = useState<FutureCalc | undefined>()
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [currentYear, setCurrentYear] = useState<number>(0)

  const theme = useTheme()

  useEffect(() => {
    if (futureCalc.futureYears.length > 0) {
      const start = futureCalc.futureYears[0].year
      setStartYear(start)
      setEndYear(futureCalc.futureYears[futureCalc.futureYears.length-1].year)
      setCurrentYear(start)
      setCalc(futureCalc)
    }
  }, [year, futureCalc])

  useEffect(() => {
    if (startYear > 0 && calc && calc.futureYears.length > 0) {
      calculateData(startYear)
    }
  }, [currentYear, calc])

  const calculateData = (startYear: number) => {
    if (!calc || startYear <= 0 || endYear < startYear) {
      return
    }
    const model = calc.model
    if (!model) {
      return
    }
    const person1 = model.person1
    let incomes1: Income[] = []
    if (person1) {
      incomes1 = model.incomes.filter((i: Income) => i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === person1.id)
    }

    const person2 = model.person2
    let incomes2: Income[] = []
    if (person2) {
      incomes2 = model.incomes.filter((i: Income) => i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === person2.id)
    }

    let primaryData1: {x:number, y:number}[] = []
    let hasPrimaryData1 = false
    let spousalData1: {x:number, y:number}[] = []
    let hasSpousalData1 = false
    let primaryData2: {x:number, y:number}[] = []
    let hasPrimaryData2 = false
    let spousalData2: {x:number, y:number}[] = []
    let hasSpousalData2 = false

    for (let index = 0; index < calc.futureYears.length; index++) {
      const year = startYear + index
      const fy = calc.futureYears[index]
      if (incomes1.length > 0) {
        const detail = fy.incomeDetails.find((d: IIncomeDetail) => d.id === incomes1[0].id)
        primaryData1.push({x: year, y: detail ? detail.amount : 0})
        if (detail && detail.amount > 0) {
          hasPrimaryData1 = true
        }
      }
      if (incomes1.length > 1) {
        const detail = fy.incomeDetails.find((d: IIncomeDetail) => d.id === incomes1[1].id)
        spousalData1.push({x: year, y: detail ? detail.amount : 0})
        if (detail && detail.amount > 0) {
          hasSpousalData1 = true
        }
      }
      if (incomes2.length > 0) {
        const detail = fy.incomeDetails.find((d: IIncomeDetail) => d.id === incomes2[0].id)
        primaryData2.push({x: year, y: detail ? detail.amount : 0})
        if (detail && detail.amount > 0) {
          hasPrimaryData2 = true
        }
      }
      if (incomes2.length > 1) {
        const detail = fy.incomeDetails.find((d: IIncomeDetail) => d.id === incomes2[1].id)
        spousalData2.push({x: year, y: detail ? detail.amount : 0})
        if (detail && detail.amount > 0) {
          hasSpousalData2 = true
        }
      }
    }

    const series: {name:string, group:string, color:string, data:any[]}[] = []

    if (person1 && incomes1.length > 0 && hasPrimaryData1) {
      series.push({name: incomes1[0].description, group: person1.nickname, color:theme.palette.primary.dark, data: primaryData1})
    }
    if (person1 && incomes1.length > 1 && hasSpousalData1) {
      series.push({name: incomes1[1].description, group: person1.nickname, color:theme.palette.primary.light, data: spousalData1})
    }

    if (person2 && incomes2.length > 0 && hasPrimaryData2) {
      series.push({name: incomes2[0].description, group: person2.nickname, color:theme.palette.secondary.dark, data: primaryData2})
    }
    if (person2 && incomes2.length > 1 && hasSpousalData2) {
      series.push({name: incomes2[1].description, group: person2.nickname, color:theme.palette.secondary.light, data: spousalData2})
    }

    setSeries(series)

    const newOptions: any = cloneDeep(defaultOptions)
    newOptions.title.text = title ?? ""
    newOptions.xaxis.min = startYear

    if (currentYear !== undefined) {
      const annotations = {
        xaxis: [
          {
            x: currentYear,
            borderColor: theme.palette.secondary.main, //'#00E396',
            label: {
              borderColor: theme.palette.secondary.main, //'#00E396',
              orientation: 'horizontal',
              text: currentYear.toString(),
              style: {
                fontSize: '14px',
                fontWeight: 600
              }
            }
          }
        ]
      }
      newOptions["annotations"] = annotations
      newOptions.chart.events["dataPointSelection"] = (event: any, chartContext: any, details: {dataPointIndex: number, seriesIndex: number}) => {
        if (details.dataPointIndex >= 0 && onChange) {
          onChange(details.dataPointIndex + startYear)
        }
      }
    }

    if (height) {
      newOptions.chart.toolbar.show = false
    }

    setOptions(newOptions)
  }

  // const handleChange = (year: number) => {
  //   setCurrentYear(year)
  //   if (onChange) {
  //     onChange(year)
  //   }
  // }

  if (options && series) {
    return (
        <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden"}}>
          <Chart
            options={options}
            series={series}
            type="bar"
            height={height ?? "500"}
            width={width ?? "100%"}
          />
        </TableContainer>
    )
  } else {
    return null
  }
}

export default SocialSecurityBenefitChart